//@flow
import * as Immutable from 'immutable';
import extend from 'lodash/extend';

import createBaseEntityReducer, {
    isRequestBodyCurrent
} from 'common/redux/reducers/baseEntityReducer';

import ZoneMapActions from 'common/redux/actions/zoneMap';

import { ZoneMap } from 'common/models/internal/ZoneMap';
import { VersionedZoneMap } from 'common/models/internal/VersionedZoneMap';

import type { ActionType } from 'common/redux/reducers/baseEntityReducer';

const baseReducer = createBaseEntityReducer(ZoneMapActions, VersionedZoneMap);

export const zoneMap = (
    state = Immutable.fromJS({ records: {} }),
    action: ActionType
) => {
    //Override with switch cases if we need to override
    switch (action.type) {
        case ZoneMapActions.types.FETCH_ENTITIES_SUCCESS: {
            const { entities, params } = action;
            if (!entities) {
                throw new Error('expected action to have entities');
            }
            const { clientId } = params;
            if (!isRequestBodyCurrent({ state, action })) {
                // reject if prevRequestBody is not same as current body
                return state;
            }
            let newState = state.setIn(
                ['records', clientId, 'entities', 'status', 'isLoading'],
                false
            );

            entities.entities = entities.maps.map(entity =>
                VersionedZoneMap.fromJS(entity)
            );

            delete entities.maps;

            newState = newState.setIn(
                ['records', clientId, 'entities', 'records'],
                entities
            );
            newState = newState.deleteIn([
                'records',
                clientId,
                'entities',
                'error'
            ]);
            return newState;
        }

        case ZoneMapActions.types.ENTITY_VERSIONS_FETCH_START: {
            const { params } = action;
            const { clientId, mapId } = params;
            return state.setIn(
                [
                    'records',
                    clientId,
                    'entities',
                    'versions',
                    mapId,
                    'status',
                    'isLoading'
                ],
                true
            );
        }
        case ZoneMapActions.types.ENTITY_VERSIONS_FETCH_FAILURE: {
            const { params } = action;
            const { clientId, mapId } = params;
            return state.setIn(
                [
                    'records',
                    clientId,
                    'entities',
                    'versions',
                    mapId,
                    'status',
                    'isLoading'
                ],
                false
            );
        }
        case ZoneMapActions.types.ENTITY_VERSIONS_FETCH_SUCCESS: {
            const { params, response } = action;
            if (!response) {
                throw new Error('expected action to have response');
            }
            const { clientId, mapId } = params;
            let newState = state.setIn(
                [
                    'records',
                    clientId,
                    'entities',
                    'versions',
                    mapId,
                    'status',
                    'isLoading'
                ],
                false
            );

            response.entities = Immutable.List(
                response.maps.map(entity => ZoneMap.fromJS(entity))
            );

            newState = newState.setIn(
                ['records', clientId, 'entities', 'versions', mapId, 'records'],
                response
            );
            newState = newState.deleteIn([
                'records',
                clientId,
                'entities',
                'versions',
                mapId,
                'error'
            ]);
            return newState;
        }

        case ZoneMapActions.types.VERSIONED_ZONE_FETCH_START: {
            const { params } = action;
            const { clientId, mapId, versionId } = params;
            return state.setIn([
                'records',
                clientId,
                'zones',
                mapId,
                'versions',
                versionId,
                'state',
                'isLoading',
                true
            ]);
        }
        case ZoneMapActions.types.VERSIONED_ZONE_FETCH_FAILURE: {
            const { params } = action;
            const { clientId, mapId, versionId } = params;
            return state.setIn([
                'records',
                clientId,
                'zones',
                mapId,
                'versions',
                versionId,
                'state',
                'isLoading',
                false
            ]);
        }

        case ZoneMapActions.types.VERSIONED_ZONE_FETCH_SUCCESS: {
            const { params, response } = action;
            if (!response) {
                throw new Error('expected action to have response');
            }
            const { clientId, mapId, versionId } = params;
            const modifiedState = state.setIn([
                'records',
                clientId,
                'zones',
                mapId,
                'versions',
                versionId,
                'state',
                'isLoading',
                false
            ]);
            return modifiedState.setIn(
                [
                    'records',
                    clientId,
                    'zones',
                    mapId,
                    'versions',
                    versionId,
                    'map'
                ],
                response
            );
        }

        case ZoneMapActions.types.MARK_MAP_ACTIVE: {
            const { params } = action;
            const { clientId, mapId, version, active } = params;

            const mapRecords = state.getIn([
                'records',
                clientId,
                'entities',
                'versions',
                mapId,
                'records'
            ]);

            const updatedVersionRecords = mapRecords.entities.map(
                (map: ZoneMap) => {
                    if (
                        map.clientId === clientId &&
                        map.id === mapId &&
                        map.version === version
                    ) {
                        return map.setIsActiveVersion(active);
                    }
                    return map.setIsActiveVersion(!active);
                }
            );

            let newState = state;

            const entitiesResponse = state.getIn([
                'records',
                clientId,
                'entities',
                'records'
            ]);

            const updatedMaps = entitiesResponse.entities.map(
                (versionedMap: VersionedZoneMap) => {
                    const { map } = versionedMap;
                    if (map.clientId === clientId && map.id === mapId) {
                        if (active) {
                            return versionedMap.setActiveVersion(version);
                        }
                        return versionedMap.setActiveVersion(null);
                    }
                    return versionedMap;
                }
            );

            newState = state.setIn(
                ['records', clientId, 'entities', 'records'],
                extend({}, entitiesResponse, {
                    entities: updatedMaps
                })
            );

            const updatedState = newState.setIn(
                ['records', clientId, 'entities', 'versions', mapId, 'records'],
                extend({}, mapRecords, {
                    entities: Immutable.List(updatedVersionRecords)
                })
            );

            return updatedState;
        }

        case ZoneMapActions.types.VERIFY_MAP_START: {
            const { params } = action;
            const { clientId } = params;
            return state.setIn(
                ['records', clientId, 'entities', 'status', 'isFullMapLoading'],
                true
            );
        }

        case ZoneMapActions.types.VERIFY_MAP_SUCCESS: {
            const { params } = action;
            const { clientId } = params;
            return state.setIn(
                ['records', clientId, 'entities', 'status', 'isFullMapLoading'],
                false
            );
        }

        case ZoneMapActions.types.VERIFY_MAP_ERROR: {
            const { params } = action;
            const { clientId } = params;
            return state.setIn(
                ['records', clientId, 'entities', 'status', 'isFullMapLoading'],
                false
            );
        }

        case ZoneMapActions.types.ADD_ENTITY_START: {
            let newState = state;
            const { params } = action;
            const { clientId } = params;
            newState = newState.setIn(
                ['records', clientId, 'entities', 'status', 'isFullMapLoading'],
                true
            );
            return newState;
        }
        case ZoneMapActions.types.ADD_ENTITY_FAILURE: {
            let newState = state;
            const { params, error } = action;
            const { clientId } = params;
            newState = newState.setIn(
                ['records', clientId, 'entities', 'status', 'isFullMapLoading'],
                false
            );
            newState = newState.setIn(
                ['records', clientId, 'entities', 'fullMapError'],
                error
            );
            return newState;
        }
        case ZoneMapActions.types.ADD_ENTITY_SUCCESS: {
            const { entity, params } = action;
            if (!entity) {
                throw new Error('expected action to have entity');
            }
            const { clientId, mapId } = params;

            const updatedZoneMap = ZoneMap.fromJS(entity);

            const entities = state.getIn(['records', clientId, 'entities']);
            const entityRecords = entities?.get('fullRecords');
            let newState = state;
            let updatedZoneMaps = Immutable.List([updatedZoneMap]);
            if (entityRecords && entityRecords.fullMaps) {
                const updatedMapIndex = entityRecords.fullMaps.findIndex(
                    map =>
                        map.clientId === updatedZoneMap.clientId &&
                        map.id === updatedZoneMap.id
                );
                if (updatedMapIndex > -1) {
                    updatedZoneMaps = updatedZoneMaps.set(
                        updatedMapIndex,
                        updatedZoneMap
                    );
                } else {
                    updatedZoneMaps = updatedZoneMaps.push(updatedZoneMap);
                }
            }

            const entityVersions: Object = entities?.getIn([
                'versions',
                mapId,
                'records'
            ]);

            if (entityVersions) {
                // Updating versions for sub table(MapVersionsTable)
                const versionIndex: number = entityVersions.entities.findIndex(
                    entityVersion =>
                        entityVersion.get('version') === entity.version
                );
                const updatedVersions: Object = {
                    ...entityVersions,
                    entities: entityVersions.entities.set(
                        versionIndex !== -1
                            ? versionIndex // existing version
                            : entityVersions.entities.size, // new version
                        ZoneMap.fromJS(entity)
                    )
                };
                newState = newState.setIn(
                    [
                        'records',
                        clientId,
                        'entities',
                        'versions',
                        mapId,
                        'records'
                    ],
                    updatedVersions
                );
            }

            newState = newState.setIn(
                ['records', clientId, 'entities', 'fullRecords'],
                { fullMaps: updatedZoneMaps }
            );
            newState = newState.setIn(
                ['records', clientId, 'entities', 'status', 'isFullMapLoading'],
                false
            );
            return newState;
        }
        case ZoneMapActions.types.USECASES_MAP_FETCH_SUCCESS: {
            const {
                params: { clientId },
                response
            } = action;

            const usecasesMap = response.usecases.reduce((mapUsecases, map) => {
                const {
                    mapId: { mapId },
                    usecases
                } = map;
                return mapUsecases.set(mapId, usecases);
            }, Immutable.Map());

            const newState = state.setIn(
                ['records', clientId, 'entities', 'usecasesMap'],
                usecasesMap
            );
            return newState;
        }

        case ZoneMapActions.types.SET_ACTIVE_USECASES_START: {
            const {
                params: { clientId, mapId }
            } = action;
            return state.setIn(
                [
                    'records',
                    clientId,
                    'entities',
                    mapId,
                    'activeUsescases',
                    'status',
                    'isLoading'
                ],
                true
            );
        }

        case ZoneMapActions.types.SET_ACTIVE_USECASES_ERROR: {
            const {
                params: { clientId, mapId }
            } = action;
            return state.setIn(
                [
                    'records',
                    clientId,
                    'entities',
                    mapId,
                    'activeUsescases',
                    'status',
                    'isLoading'
                ],
                false
            );
        }

        case ZoneMapActions.types.SET_ACTIVE_USECASES_SUCCESS: {
            const {
                params: { clientId, mapId }
            } = action;
            let newState = state;
            newState = state.setIn(
                [
                    'records',
                    clientId,
                    'entities',
                    mapId,
                    'activeUsescases',
                    'status',
                    'isLoading'
                ],
                false
            );
            return newState;
        }

        case ZoneMapActions.types.VERIFY_MAP_ACCESS_FETCH_SUCCESS: {
            const {
                params: { clientId },
                response
            } = action;
            const newState = state.setIn(
                ['records', clientId, 'entities', 'entityAccess'],
                response
            );
            return newState;
        }

        case ZoneMapActions.types.DISABLE_ENTITY_START: {
            const {
                params: { clientId }
            } = action;
            return state.setIn(
                ['records', clientId, 'entities', 'status', 'isDeletingEntity'],
                true
            );
        }
        case ZoneMapActions.types.DISABLE_ENTITY_SUCCESS:
        case ZoneMapActions.types.DISABLE_ENTITY_FAILURE: {
            const {
                params: { clientId }
            } = action;
            return state.setIn(
                ['records', clientId, 'entities', 'status', 'isDeletingEntity'],
                false
            );
        }
    }
    return baseReducer(state, action);
};
